import React from 'react';
import {
  BLOCK_LIBRARY,
  CTA_PRESETS,
  EMAIL_TEMPLATES,
  createBlock,
  getCtaPreset
} from '../utils/newsletterEmailBuilder';
import './NewsletterBuilder.css';

/**
 * Constructor por bloques para campañas de newsletter.
 * Controlado por el padre: recibe blocks/design y emite cambios.
 * El HTML email-safe se genera fuera (buildEmailHtml) a partir de estos datos.
 */

const BLOCK_LABELS = BLOCK_LIBRARY.reduce((acc, b) => {
  acc[b.type] = b;
  return acc;
}, {});

function Field({ label, hint, children }) {
  return (
    <label className="nb-field">
      <span className="nb-field-label">{label}</span>
      {children}
      {hint ? <span className="nb-field-hint">{hint}</span> : null}
    </label>
  );
}

function CtaPicker({ block, onPatch, prefix = '' }) {
  const preset = getCtaPreset(block.ctaType);
  return (
    <div className="nb-cta-row">
      <Field label={`${prefix}Acción del botón`}>
        <select
          className="nb-input"
          value={block.ctaType || 'web'}
          onChange={(e) => onPatch({ ctaType: e.target.value })}
        >
          {CTA_PRESETS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label={`${prefix}Destino`} hint={preset.hint}>
        <input
          className="nb-input"
          type="text"
          placeholder={preset.placeholder}
          value={block.ctaUrl || ''}
          onChange={(e) => onPatch({ ctaUrl: e.target.value })}
        />
      </Field>
    </div>
  );
}

function BlockEditor({ block, onPatch, onOpenProductPicker }) {
  switch (block.type) {
    case 'hero':
      return (
        <div className="nb-block-fields">
          <Field label="Imagen hero (URL)" hint="Pegá la URL de una imagen impactante. Usá ancho ~1200px.">
            <input className="nb-input" type="text" value={block.image || ''} onChange={(e) => onPatch({ image: e.target.value })} placeholder="https://..." />
          </Field>
          <Field label="Texto alternativo (alt)" hint="Importante para entregabilidad y accesibilidad.">
            <input className="nb-input" type="text" value={block.alt || ''} onChange={(e) => onPatch({ alt: e.target.value })} />
          </Field>
          <Field label="Etiqueta superior (eyebrow)">
            <input className="nb-input" type="text" value={block.eyebrow || ''} onChange={(e) => onPatch({ eyebrow: e.target.value })} placeholder="NUEVA COLECCIÓN" />
          </Field>
          <Field label="Título" hint="Corto y fuerte. Usá Enter para saltos de línea.">
            <textarea className="nb-input nb-textarea" rows={2} value={block.title || ''} onChange={(e) => onPatch({ title: e.target.value })} />
          </Field>
          <Field label="Subtítulo">
            <textarea className="nb-input nb-textarea" rows={2} value={block.subtitle || ''} onChange={(e) => onPatch({ subtitle: e.target.value })} />
          </Field>
          <div className="nb-cta-row">
            <Field label="Precio / Oferta">
              <input className="nb-input" type="text" value={block.price || ''} onChange={(e) => onPatch({ price: e.target.value })} placeholder="$19.999" />
            </Field>
            <Field label="Nota del precio">
              <input className="nb-input" type="text" value={block.priceNote || ''} onChange={(e) => onPatch({ priceNote: e.target.value })} placeholder="antes $29.999" />
            </Field>
          </div>
          <Field label="Texto del botón">
            <input className="nb-input" type="text" value={block.ctaLabel || ''} onChange={(e) => onPatch({ ctaLabel: e.target.value })} placeholder="VER COLECCIÓN" />
          </Field>
          <CtaPicker block={block} onPatch={onPatch} />
        </div>
      );
    case 'offer':
      return (
        <div className="nb-block-fields">
          <Field label="Badge">
            <input className="nb-input" type="text" value={block.badge || ''} onChange={(e) => onPatch({ badge: e.target.value })} placeholder="OFERTA" />
          </Field>
          <Field label="Título de la oferta">
            <input className="nb-input" type="text" value={block.title || ''} onChange={(e) => onPatch({ title: e.target.value })} />
          </Field>
          <Field label="Texto">
            <textarea className="nb-input nb-textarea" rows={2} value={block.text || ''} onChange={(e) => onPatch({ text: e.target.value })} />
          </Field>
        </div>
      );
    case 'gif':
      return (
        <div className="nb-block-fields">
          <Field label="GIF / Imagen (URL)" hint="Soporta GIF animado. Para video, exportá un GIF o usá un frame como preview.">
            <input className="nb-input" type="text" value={block.image || ''} onChange={(e) => onPatch({ image: e.target.value })} placeholder="https://...gif" />
          </Field>
          <Field label="Texto alternativo (alt)">
            <input className="nb-input" type="text" value={block.alt || ''} onChange={(e) => onPatch({ alt: e.target.value })} />
          </Field>
          <label className="nb-checkbox">
            <input type="checkbox" checked={!!block.fakePlay} onChange={(e) => onPatch({ fakePlay: e.target.checked })} />
            <span>Mostrar botón de play falso (efecto video)</span>
          </label>
          <Field label="Epígrafe (opcional)">
            <input className="nb-input" type="text" value={block.caption || ''} onChange={(e) => onPatch({ caption: e.target.value })} />
          </Field>
          <CtaPicker block={block} onPatch={onPatch} prefix="Al hacer clic: " />
        </div>
      );
    case 'button':
      return (
        <div className="nb-block-fields">
          <Field label="Texto del botón">
            <input className="nb-input" type="text" value={block.label || ''} onChange={(e) => onPatch({ label: e.target.value })} />
          </Field>
          <Field label="Alineación">
            <select className="nb-input" value={block.align || 'center'} onChange={(e) => onPatch({ align: e.target.value })}>
              <option value="left">Izquierda</option>
              <option value="center">Centro</option>
              <option value="right">Derecha</option>
            </select>
          </Field>
          <CtaPicker block={block} onPatch={onPatch} />
        </div>
      );
    case 'countdown':
      return (
        <div className="nb-block-fields">
          <Field label="Título">
            <input className="nb-input" type="text" value={block.title || ''} onChange={(e) => onPatch({ title: e.target.value })} />
          </Field>
          <Field label="Fecha límite" hint="El contador se calcula al generar el email (los emails no actualizan en vivo).">
            <input className="nb-input" type="datetime-local" value={block.endDate || ''} onChange={(e) => onPatch({ endDate: e.target.value })} />
          </Field>
        </div>
      );
    case 'testimonial':
      return (
        <div className="nb-block-fields">
          <Field label="Cita">
            <textarea className="nb-input nb-textarea" rows={3} value={block.quote || ''} onChange={(e) => onPatch({ quote: e.target.value })} />
          </Field>
          <div className="nb-cta-row">
            <Field label="Autor">
              <input className="nb-input" type="text" value={block.author || ''} onChange={(e) => onPatch({ author: e.target.value })} />
            </Field>
            <Field label="Rol / detalle">
              <input className="nb-input" type="text" value={block.role || ''} onChange={(e) => onPatch({ role: e.target.value })} />
            </Field>
          </div>
        </div>
      );
    case 'products':
      return (
        <div className="nb-block-fields">
          <Field label="Título de la sección">
            <input className="nb-input" type="text" value={block.title || ''} onChange={(e) => onPatch({ title: e.target.value })} />
          </Field>
          <div className="nb-products-list">
            {(block.items || []).length === 0 ? (
              <p className="nb-muted">Sin productos. Agregá desde el catálogo.</p>
            ) : (
              (block.items || []).map((it, idx) => (
                <div key={it.id || idx} className="nb-product-chip">
                  {it.image ? <img src={it.image} alt={it.name || ''} className="nb-product-thumb" /> : <span className="nb-product-thumb nb-product-thumb-empty" />}
                  <span className="nb-product-name">{it.name || 'Producto'}</span>
                  <button
                    type="button"
                    className="nb-chip-remove"
                    onClick={() => onPatch({ items: (block.items || []).filter((_, i) => i !== idx) })}
                    title="Quitar producto"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
          <button type="button" className="nb-btn nb-btn-secondary" onClick={() => onOpenProductPicker(block.id)}>
            + Agregar productos del catálogo
          </button>
        </div>
      );
    case 'text':
      return (
        <div className="nb-block-fields">
          <Field label="Título (opcional)">
            <input className="nb-input" type="text" value={block.title || ''} onChange={(e) => onPatch({ title: e.target.value })} />
          </Field>
          <Field label="Texto">
            <textarea className="nb-input nb-textarea" rows={4} value={block.text || ''} onChange={(e) => onPatch({ text: e.target.value })} />
          </Field>
        </div>
      );
    default:
      return null;
  }
}

const NewsletterBuilder = ({
  blocks = [],
  design = {},
  onBlocksChange,
  onDesignChange,
  onOpenProductPicker
}) => {
  const updateDesign = (patch) => onDesignChange({ ...design, ...patch });

  const addBlock = (type) => {
    onBlocksChange([...blocks, createBlock(type)]);
  };

  const patchBlock = (id, patch) => {
    onBlocksChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const removeBlock = (id) => {
    onBlocksChange(blocks.filter((b) => b.id !== id));
  };

  const moveBlock = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= blocks.length) return;
    const copy = [...blocks];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    onBlocksChange(copy);
  };

  const applyTemplate = (templateId) => {
    const tpl = EMAIL_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    const { design: tplDesign, blocks: tplBlocks } = tpl.build();
    onDesignChange(tplDesign);
    onBlocksChange(tplBlocks);
  };

  return (
    <div className="nb-root">
      {/* Plantillas */}
      <div className="nb-section">
        <div className="nb-section-title">Empezar con una plantilla</div>
        <div className="nb-template-grid">
          {EMAIL_TEMPLATES.map((tpl) => (
            <button key={tpl.id} type="button" className="nb-template-card" onClick={() => applyTemplate(tpl.id)}>
              <span className="nb-template-name">{tpl.name}</span>
              <span className="nb-template-desc">{tpl.desc}</span>
            </button>
          ))}
          <button type="button" className="nb-template-card nb-template-blank" onClick={() => onBlocksChange([])}>
            <span className="nb-template-name">Desde cero</span>
            <span className="nb-template-desc">Vaciar y construir a mano</span>
          </button>
        </div>
      </div>

      {/* Diseño global */}
      <div className="nb-section">
        <div className="nb-section-title">Diseño</div>
        <div className="nb-design-grid">
          <Field label="Marca (header)">
            <input className="nb-input" type="text" value={design.brandName || ''} onChange={(e) => updateDesign({ brandName: e.target.value })} />
          </Field>
          <Field label="Logo (URL, opcional)">
            <input className="nb-input" type="text" value={design.logoUrl || ''} onChange={(e) => updateDesign({ logoUrl: e.target.value })} placeholder="https://..." />
          </Field>
          <Field label="Color principal">
            <span className="nb-color-row">
              <input className="nb-color" type="color" value={design.primaryColor || '#3b82f6'} onChange={(e) => updateDesign({ primaryColor: e.target.value })} />
              <input className="nb-input" type="text" value={design.primaryColor || ''} onChange={(e) => updateDesign({ primaryColor: e.target.value })} />
            </span>
          </Field>
          <label className="nb-checkbox nb-checkbox-design">
            <input type="checkbox" checked={!!design.darkMode} onChange={(e) => updateDesign({ darkMode: e.target.checked })} />
            <span>Modo oscuro (email premium)</span>
          </label>
        </div>
      </div>

      {/* Bloques */}
      <div className="nb-section">
        <div className="nb-section-title">Bloques de la campaña</div>
        {blocks.length === 0 && <p className="nb-muted">Agregá bloques para construir tu email.</p>}
        <div className="nb-blocks">
          {blocks.map((block, index) => {
            const meta = BLOCK_LABELS[block.type] || { icon: '🔧', label: block.type };
            return (
              <div key={block.id} className="nb-block">
                <div className="nb-block-head">
                  <span className="nb-block-title">
                    <span className="nb-block-icon">{meta.icon}</span> {meta.label}
                  </span>
                  <div className="nb-block-actions">
                    <button type="button" className="nb-icon-btn" title="Subir" disabled={index === 0} onClick={() => moveBlock(index, -1)}>↑</button>
                    <button type="button" className="nb-icon-btn" title="Bajar" disabled={index === blocks.length - 1} onClick={() => moveBlock(index, 1)}>↓</button>
                    <button type="button" className="nb-icon-btn nb-icon-btn-danger" title="Eliminar bloque" onClick={() => removeBlock(block.id)}>🗑️</button>
                  </div>
                </div>
                <BlockEditor
                  block={block}
                  onPatch={(patch) => patchBlock(block.id, patch)}
                  onOpenProductPicker={onOpenProductPicker}
                />
              </div>
            );
          })}
        </div>

        <div className="nb-add-block">
          <span className="nb-add-block-label">Agregar bloque:</span>
          <div className="nb-add-block-grid">
            {BLOCK_LIBRARY.map((b) => (
              <button key={b.type} type="button" className="nb-add-chip" title={b.desc} onClick={() => addBlock(b.type)}>
                <span className="nb-add-chip-icon">{b.icon}</span>
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterBuilder;
